import { useState } from "react";
import { Brand } from "../components/Brand";
import { Banner } from "../components/ui";
import { ApiError, type Api } from "../lib/api";
import { parseTokenizedQuery } from "../lib/queryParams";

type State = "ready" | "success";

export function Csat({ api }: { api: Api }) {
  const params = parseTokenizedQuery(window.location.search);
  const ticketId = params.get("ticket") ?? "";
  const token = params.get("token") ?? "";

  const [score, setScore] = useState<number>(5);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState<State>("ready");
  const [error, setError] = useState<string | null>(null);

  const canSubmit = ticketId !== "" && token !== "";

  async function submit() {
    if (!canSubmit) {
      setError("This CSAT link is missing its ticket or token.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.submitCsat(ticketId, token, score, comment.trim() || undefined);
      setState("success");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not submit your feedback.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <Brand />
        </div>
        <div className="card p-8">
          <div className="mb-6">
            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-accent">Support survey</div>
            <h1 className="mt-1 text-xl font-semibold text-ink">Rate your support experience</h1>
            <p className="mt-1 text-sm text-ink-muted">
              Share how PumaMesh support handled your ticket.
            </p>
          </div>

          {error && (
            <div className="mb-4">
              <Banner tone="error">{error}</Banner>
            </div>
          )}

          {state === "success" ? (
            <Banner tone="success">Thanks for your feedback. Your response has been recorded.</Banner>
          ) : (
            <>
              {!canSubmit && (
                <div className="mb-4">
                  <Banner tone="error">This CSAT link is missing its ticket or token.</Banner>
                </div>
              )}
              <div>
                <label className="label">Rating</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      className={score === n ? "btn-primary h-10 w-10" : "btn-ghost h-10 w-10"}
                      disabled={busy}
                      onClick={() => setScore(n)}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mt-4">
                <label className="label">Comment (optional)</label>
                <textarea
                  className="input min-h-[96px]"
                  disabled={busy}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Tell us what worked well or what we can improve."
                />
              </div>
              <div className="mt-4 flex justify-end">
                <button className="btn-primary" disabled={busy || !canSubmit} onClick={() => void submit()}>
                  {busy ? "Submitting…" : "Submit"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
